import React, { useState, useEffect, useCallback } from 'react';
import { ROOM_TYPES, BED_TYPES, ROOM_STATUS_OPTIONS, BED_STATUS_OPTIONS, BedStatus, RoomType } from '../../types/enums';
import './DepartmentManagement.css';
import { addBed, updateBed, deleteBed, getBedsByRoom } from '../../services/bedService';
import FacilityManagementService from '../../services/facilityManagementService';
import { v4 as uuidv4 } from 'uuid';

const DepartmentManagement = ({ facilityId }) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [selectedRoomCode, setSelectedRoomCode] = useState(null);
  const [bedDialogOpen, setBedDialogOpen] = useState(false);
  const [editingBed, setEditingBed] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    status: 'active',
    defaultRoom: ''
  });
  const [roomFormData, setRoomFormData] = useState({
    name: '',
    code: '',
    type: '',
    capacity: '',
    status: 'active',
    notes: ''
  });
  const [bedFormData, setBedFormData] = useState({
    name: '',
    code: '',
    type: '',
    status: BedStatus.AVAILABLE,
    notes: ''
  });
  const [beds, setBeds] = useState([]); // Add beds state
  const [bedsLoading, setBedsLoading] = useState(false); // Add beds loading state


  const loadDepartments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const facilityService = new FacilityManagementService();
      const facilityData = await facilityService.getFacility(facilityId);
      console.log('Loaded facility data:', facilityData);
      console.log('Departments:', facilityData.departments);
      if (facilityData.departments) {
        facilityData.departments.forEach(dept => {
          console.log(`Department ${dept.name}:`, dept);
          if (dept.rooms) {
            dept.rooms.forEach(room => {
              console.log(`Room ${room.name} (${room.code}):`, room);
              console.log(`Room type: ${room.type}, Beds:`, room.beds);
            });
          }
        });
      }
      setDepartments(facilityData.departments || []);
    } catch (err) {
      console.error('Error loading departments:', err);
      setError('Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    if (facilityId) {
      loadDepartments();
    }
  }, [facilityId, loadDepartments]);

  const generateDepartmentId = () => {
    const maxId = departments.length > 0 
      ? Math.max(...departments.map(dept => parseInt(dept.id) || 0))
      : 0;
    return (maxId + 1).toString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const facilityService = new FacilityManagementService();
      let updatedDepartments;
      
      if (editingDepartment) {
        updatedDepartments = departments.map(dept =>
          dept.id === editingDepartment.id ? { ...formData, id: dept.id, rooms: dept.rooms || [] } : dept
        );
      } else {
        const newDepartment = {
          ...formData,
          id: generateDepartmentId(),
          createdAt: new Date().toISOString(),
          rooms: []
        };
        updatedDepartments = [...departments, newDepartment];
      }
      
      await facilityService.updateFacilityDepartments(facilityId, updatedDepartments);
      setDepartments(updatedDepartments);
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving department:', err);
      setError('Failed to save department');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name || '',
      code: department.code || '',
      description: department.description || '',
      status: department.status || 'active',
      defaultRoom: department.defaultRoom || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (departmentId) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;

    setLoading(true);
    setError(null);

    try {
      const facilityService = new FacilityManagementService();
      const updatedDepartments = departments.filter(dept => dept.id !== departmentId);
      await facilityService.updateFacilityDepartments(facilityId, updatedDepartments);
      setDepartments(updatedDepartments);
      if (selectedDepartmentId === departmentId) {
        setSelectedDepartmentId(null);
      }
    } catch (err) {
      console.error('Error deleting department:', err);
      setError('Failed to delete department');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDepartment(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      status: 'active',
      defaultRoom: ''
    });
    setError(null);
  };

  const handleAddNew = () => {
    setEditingDepartment(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      status: 'active',
      defaultRoom: ''
    });
    setDialogOpen(true);
  };

  const handleRowClick = (departmentId, e) => {
    if (e.target.closest('.dept-actions')) return;
    setSelectedDepartmentId(departmentId === selectedDepartmentId ? null : departmentId);
  };

  // Room CRUD functions
  const handleAddRoom = () => {
    setEditingRoom(null);
    setRoomFormData({
      name: '',
      code: '',
      type: '',
      capacity: '',
      status: 'active',
      notes: ''
    });
    setRoomDialogOpen(true);
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room);
    setRoomFormData({
      name: room.name || '',
      code: room.code || '',
      type: room.type || '',
      capacity: room.capacity || '',
      status: room.status || 'active',
      notes: room.notes || ''
    });
    setRoomDialogOpen(true);
  };

  const handleDeleteRoom = async (roomCode) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;

    setLoading(true);
    setError(null);

    try {
      const updatedDepartments = departments.map(dept => {
        if (dept.id === selectedDepartmentId) {
          return {
            ...dept,
            rooms: dept.rooms.filter(room => room.code !== roomCode)
          };
        }
        return dept;
      });

      const facilityService = new FacilityManagementService();
      await facilityService.updateFacilityDepartments(facilityId, updatedDepartments);
      setDepartments(updatedDepartments);
    } catch (err) {
      console.error('Error deleting room:', err);
      setError('Failed to delete room');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const updatedDepartments = departments.map(dept => {
        if (dept.id === selectedDepartmentId) {
          let updatedRooms;
          if (editingRoom) {
            // Update existing room
            updatedRooms = dept.rooms.map(room => 
              room.code === editingRoom.code ? { ...roomFormData } : room
            );
          } else {
            // Add new room
            updatedRooms = [...(dept.rooms || []), { ...roomFormData }];
          }
          return { ...dept, rooms: updatedRooms };
        }
        return dept;
      });

      const facilityService = new FacilityManagementService();
      await facilityService.updateFacilityDepartments(facilityId, updatedDepartments);
      setDepartments(updatedDepartments);
      handleCloseRoomDialog();
    } catch (err) {
      console.error('Error saving room:', err);
      setError('Failed to save room');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseRoomDialog = () => {
    setRoomDialogOpen(false);
    setEditingRoom(null);
    setRoomFormData({
      name: '',
      code: '',
      type: '',
      capacity: '',
      status: 'active',
      notes: ''
    });
  };

  // Handle room type change to auto-set capacity for stand-alone rooms
  const handleRoomTypeChange = (type) => {
    setRoomFormData(prev => ({ 
      ...prev, 
      type,
      // Auto-set capacity to 1 for stand-alone rooms
      capacity: type === RoomType.STANDALONE_ROOM ? '1' : prev.capacity
    }));
  };

  // Check if room is stand-alone (no beds allowed)
  const isStandaloneRoom = (room) => {
    if (!room) return false;
    return room.type === RoomType.STANDALONE_ROOM;
  };

  // Bed CRUD functions
  const handleAddBed = async () => {
    setEditingBed(null);
    setBedFormData({
      name: '',
      code: '',
      type: '',
      status: BedStatus.AVAILABLE,
      notes: ''
    });
    setBedDialogOpen(true);
  };

  const handleEditBed = (bed) => {
    setEditingBed(bed);
    setBedFormData({
      name: bed.name || '',
      code: bed.code || '',
      type: bed.type || '',
      status: bed.status || 'active',
      notes: bed.notes || ''
    });
    setBedDialogOpen(true);
  };

  const handleDeleteBed = async (bedId) => {
    if (!window.confirm('Are you sure you want to delete this bed?')) return;
    setLoading(true);
    setError(null);
    try {
      // Delete from separate collection
      await deleteBed(bedId);
      
      // Refresh beds for the current room
      await loadBedsForRoom(selectedRoomCode);
      
    } catch (err) {
      console.error('Error deleting bed:', err);
      setError('Failed to delete bed');
    } finally {
      setLoading(false);
    }
  };

  const handleBedSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // Save bed to separate collection first
      if (editingBed) {
        await updateBed(editingBed.id, bedFormData);
      } else {
        await addBed({
          facilityId,
          departmentId: selectedDepartmentId,
          roomId: selectedRoomCode,
          ...bedFormData
        });
      }
      
      // Refresh beds for the current room
      await loadBedsForRoom(selectedRoomCode);
      
      handleCloseBedDialog();
    } catch (err) {
      console.error('Error saving bed:', err);
      setError('Failed to save bed');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseBedDialog = () => {
    setBedDialogOpen(false);
    setEditingBed(null);
    setBedFormData({
      name: '',
      code: '',
      type: '',
      status: BedStatus.AVAILABLE,
      notes: ''
    });
  };

  // Load beds for selected room
  const loadBedsForRoom = useCallback(async (roomCode) => {
    if (!roomCode || !selectedDepartmentId) return;
    
    setBedsLoading(true);
    try {
      const roomBeds = await getBedsByRoom(roomCode, facilityId);
      console.log('Loaded beds for room:', roomCode, roomBeds);
      setBeds(roomBeds || []);
    } catch (err) {
      console.error('Error loading beds for room:', err);
      setBeds([]);
    } finally {
      setBedsLoading(false);
    }
  }, [selectedDepartmentId, facilityId]);

  // Update beds when room selection changes
  useEffect(() => {
    if (selectedRoomCode) {
      loadBedsForRoom(selectedRoomCode);
    } else {
      setBeds([]);
    }
  }, [selectedRoomCode, loadBedsForRoom]);

  const handleRoomClick = (roomCode, e) => {
    if (e.target.closest('.room-actions')) return;
    console.log('Room clicked:', roomCode, 'Current selected:', selectedRoomCode);
    console.log('Selected department:', selectedDepartment);
    console.log('Room data:', selectedDepartment?.rooms?.find(r => r.code === roomCode));
    setSelectedRoomCode(roomCode === selectedRoomCode ? null : roomCode);
  };

  const selectedDepartment = departments.find(d => d.id === selectedDepartmentId);

  if (loading && departments.length === 0) {
    return <div className="dept-loading">Loading departments...</div>;
  }

  return (
    <div className="dept-container">
      <div className="dept-header">
        <h5>Departments</h5>
        <button className="dept-btn-add" onClick={handleAddNew} disabled={loading}>
          ➕ Add Department
        </button>
      </div>

      {error && <div className="dept-error">{error}</div>}

      {departments.length === 0 ? (
        <div className="dept-empty">No departments found</div>
      ) : (
        <div className="dept-table">
          <div className="dept-table-header">
            <div>ID</div>
            <div>Name</div>
            <div>Code</div>
            <div>Status</div>
            <div>Actions</div>
          </div>
          {departments.map((department) => (
            <div 
              key={department.id}
              className={`dept-table-row ${selectedDepartmentId === department.id ? 'selected' : ''}`}
              onClick={(e) => handleRowClick(department.id, e)}
            >
              <div>{department.id}</div>
              <div>{department.name}</div>
              <div>{department.code}</div>
              <div>
                <span className={`dept-status ${department.status}`}>
                  {department.status}
                </span>
              </div>
              <div className="dept-actions">
                <button 
                  className="dept-btn-edit" 
                  onClick={(e) => { e.stopPropagation(); handleEdit(department); }}
                  disabled={loading}
                >
                  ✏️
                </button>
                <button 
                  className="dept-btn-delete" 
                  onClick={(e) => { e.stopPropagation(); handleDelete(department.id); }}
                  disabled={loading}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

             {/* Show rooms for selected department */}
       {selectedDepartment && (
         <div className="dept-rooms">
           <div className="rooms-header">
             <h6>Rooms in {selectedDepartment.name}</h6>
             <button className="room-btn-add" onClick={handleAddRoom} disabled={loading}>
               ➕ Add Room
             </button>
           </div>
           
           {selectedDepartment.rooms && selectedDepartment.rooms.length > 0 ? (
             <div className="rooms-table">
               <div className="rooms-table-header">
                 <div>Name</div>
                 <div>Code</div>
                 <div>Type</div>
                 <div>Capacity</div>
                 <div>Status</div>
                 <div>Actions</div>
               </div>
               {selectedDepartment.rooms.map((room, index) => (
                 <div 
                   key={index} 
                   className={`rooms-table-row ${selectedRoomCode === room.code ? 'selected' : ''}`}
                   onClick={(e) => handleRoomClick(room.code, e)}
                 >
                   <div><strong>{room.name}</strong></div>
                   <div>{room.code}</div>
                   <div>{room.type}</div>
                   <div>{room.capacity}</div>
                   <div>
                     <span className={`room-status ${room.status}`}>
                       {room.status}
                     </span>
                   </div>
                   <div className="room-actions">
                     <button 
                       className="room-btn-edit" 
                       onClick={() => handleEditRoom(room)}
                       disabled={loading}
                       title="Edit room"
                     >
                       ✏️
                     </button>
                     <button 
                       className="room-btn-delete" 
                       onClick={() => handleDeleteRoom(room.code)}
                       disabled={loading}
                       title="Delete room"
                     >
                       🗑️
                     </button>
                   </div>
                 </div>
               ))}
             </div>
           ) : (
             <div className="dept-empty">
               No rooms in this department yet
               <button className="dept-empty-add" onClick={handleAddRoom} disabled={loading}>
                 ➕ Add First Room
               </button>
             </div>
           )}

           {/* Show beds for selected room */}
           {selectedRoomCode && (
             <div className="dept-beds">
               {console.log('Beds panel rendering for room:', selectedRoomCode)}
               {console.log('Selected department rooms:', selectedDepartment?.rooms)}
               {console.log('Selected room:', selectedDepartment?.rooms?.find(r => r.code === selectedRoomCode))}
               {console.log('Beds state:', beds)}
               <div className="beds-header">
                 <h6>Beds in {selectedDepartment.rooms.find(r => r.code === selectedRoomCode)?.name || selectedRoomCode}</h6>
                 {(() => {
                   const selectedRoom = selectedDepartment.rooms.find(r => r.code === selectedRoomCode);
                   console.log('Checking if room is standalone:', selectedRoom, isStandaloneRoom(selectedRoom));
                   return !isStandaloneRoom(selectedRoom) ? (
                     <button className="bed-btn-add" onClick={handleAddBed} disabled={loading || bedsLoading}>
                       ➕ Add Bed
                     </button>
                   ) : null;
                 })()}
               </div>
               
               {(() => {
                 const selectedRoom = selectedDepartment.rooms.find(r => r.code === selectedRoomCode);
                 
                 console.log('Selected room:', selectedRoom, 'Beds:', beds);
                 console.log('Room type:', selectedRoom?.type);
                 console.log('Is standalone:', isStandaloneRoom(selectedRoom));
                 
                 if (isStandaloneRoom(selectedRoom)) {
                   return (
                     <div className="dept-empty">
                       <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                         <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>🏥</div>
                         <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Stand-alone Room</div>
                         <div style={{ fontSize: '0.9rem' }}>
                           This room operates at room-level assignment.<br/>
                           No beds can be added to stand-alone rooms.
                         </div>
                       </div>
                     </div>
                   );
                 }
                 
                 if (bedsLoading) {
                   return (
                     <div className="dept-loading">
                       <div className="dept-loading-spinner"></div>
                       <div className="dept-loading-text">Loading beds...</div>
                     </div>
                   );
                 }
                 
                 return beds.length > 0 ? (
                   <div className="beds-table">
                     <div className="beds-table-header">
                       <div>Name</div>
                       <div>Code</div>
                       <div>Type</div>
                       <div>Status</div>
                       <div>Actions</div>
                     </div>
                     {beds.map((bed, index) => (
                       <div key={bed.id || index} className="beds-table-row">
                         <div>{bed.name}</div>
                         <div>{bed.code}</div>
                         <div>{bed.type}</div>
                         <div>
                           <span className={`bed-status ${bed.status}`}>
                             {bed.status}
                           </span>
                         </div>
                         <div className="bed-actions">
                           <button 
                             className="bed-btn-edit" 
                             onClick={() => handleEditBed(bed)}
                             disabled={loading}
                             title="Edit bed"
                           >
                             ✏️
                           </button>
                           <button 
                             className="bed-btn-delete" 
                             onClick={() => handleDeleteBed(bed.id)}
                             disabled={loading}
                             title="Delete bed"
                           >
                             🗑️
                           </button>
                         </div>
                       </div>
                     ))}
                   </div>
                 ) : (
                   <div className="dept-empty">
                     <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                       <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>🛏️</div>
                       <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>No Beds Yet</div>
                       <div style={{ fontSize: '0.9rem', marginBottom: '15px' }}>
                         This room doesn't have any beds yet.<br/>
                         Add beds to enable bed-level assignments.
                       </div>
                       <button className="dept-empty-add" onClick={handleAddBed} disabled={loading}>
                         ➕ Add First Bed
                       </button>
                     </div>
                   </div>
                 );
               })()}
             </div>
           )}

           {!selectedRoomCode && selectedDepartment.rooms && selectedDepartment.rooms.length > 0 && (
             <div className="dept-hint">
               💡 Select a room to see the beds
             </div>
           )}
         </div>
       )}

      {!selectedDepartmentId && departments.length > 0 && (
        <div className="dept-hint">
          💡 Click on a department row to view its rooms
        </div>
      )}

      {/* Add/Edit Dialog */}
      {dialogOpen && (
        <div className="dept-dialog-overlay" onClick={handleCloseDialog}>
          <div className="dept-dialog" onClick={e => e.stopPropagation()}>
            <div className="dept-dialog-header">
              <h6>{editingDepartment ? 'Edit Department' : 'Add Department'}</h6>
              <button className="dept-dialog-close" onClick={handleCloseDialog}>✕</button>
            </div>
            
            <form onSubmit={handleSubmit} className="dept-dialog-form">
              <div className="dept-dialog-field">
                <label>Department Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="dept-dialog-field">
                <label>Department Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                  required
                  disabled={loading}
                  placeholder="e.g., ED, CARD, RAD"
                />
              </div>
              
              <div className="dept-dialog-field">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={loading}
                  rows={2}
                />
              </div>
              
              <div className="dept-dialog-field">
                <label>Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  required
                  disabled={loading}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="dept-dialog-actions">
                <button type="button" className="dept-btn-cancel" onClick={handleCloseDialog}>
                  Cancel
                </button>
                <button type="submit" className="dept-btn-save" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
                 </div>
       )}

       {/* Room Add/Edit Dialog */}
       {roomDialogOpen && (
         <div className="dept-dialog-overlay" onClick={handleCloseRoomDialog}>
           <div className="dept-dialog" onClick={e => e.stopPropagation()}>
             <div className="dept-dialog-header">
               <h6>{editingRoom ? 'Edit Room' : 'Add Room'}</h6>
               <button className="dept-dialog-close" onClick={handleCloseRoomDialog}>✕</button>
             </div>
             
             <form onSubmit={handleRoomSubmit} className="dept-dialog-form">
               <div className="dept-dialog-row">
                 <div className="dept-dialog-field">
                   <label>Room Name *</label>
                   <input
                     type="text"
                     value={roomFormData.name}
                     onChange={(e) => setRoomFormData(prev => ({ ...prev, name: e.target.value }))}
                     required
                     disabled={loading}
                     placeholder="e.g., Room 101, OR 1"
                   />
                 </div>
                 
                 <div className="dept-dialog-field">
                   <label>Room Code *</label>
                   <input
                     type="text"
                     value={roomFormData.code}
                     onChange={(e) => setRoomFormData(prev => ({ ...prev, code: e.target.value }))}
                     required
                     disabled={loading}
                     placeholder="e.g., R101, OR1"
                   />
                 </div>
               </div>

               <div className="dept-dialog-row">
                 <div className="dept-dialog-field">
                   <label>Room Type *</label>
                   <select
                     value={roomFormData.type}
                     onChange={(e) => handleRoomTypeChange(e.target.value)}
                     required
                     disabled={loading}
                   >
                     <option value="">Select type...</option>
                     {ROOM_TYPES.map((type) => (
                       <option key={type} value={type}>{type}</option>
                     ))}
                   </select>
                 </div>
                 
                 <div className="dept-dialog-field">
                   <label>Capacity</label>
                   <input
                     type="number"
                     value={roomFormData.capacity}
                     onChange={(e) => setRoomFormData(prev => ({ ...prev, capacity: e.target.value }))}
                     disabled={loading || roomFormData.type === RoomType.STANDALONE_ROOM}
                     min="1"
                     placeholder="1"
                   />
                   {roomFormData.type === RoomType.STANDALONE_ROOM && (
                     <small style={{ color: '#666', fontSize: '0.8rem' }}>
                       Stand-alone rooms have capacity of 1 and cannot contain beds
                     </small>
                   )}
                 </div>
               </div>

               <div className="dept-dialog-row">
                 <div className="dept-dialog-field">
                   <label>Status *</label>
                   <select
                     value={roomFormData.status}
                     onChange={(e) => setRoomFormData(prev => ({ ...prev, status: e.target.value }))}
                     required
                     disabled={loading}
                   >
                     {ROOM_STATUS_OPTIONS.map((option) => (
                       <option key={option.value} value={option.value}>{option.label}</option>
                     ))}
                   </select>
                 </div>
                 
                 <div className="dept-dialog-field">
                   <label>Notes</label>
                   <input
                     type="text"
                     value={roomFormData.notes}
                     onChange={(e) => setRoomFormData(prev => ({ ...prev, notes: e.target.value }))}
                     disabled={loading}
                     placeholder="Additional notes..."
                   />
                 </div>
               </div>
               
               <div className="dept-dialog-actions">
                 <button type="button" className="dept-btn-cancel" onClick={handleCloseRoomDialog}>
                   Cancel
                 </button>
                 <button type="submit" className="dept-btn-save" disabled={loading}>
                   {loading ? 'Saving...' : 'Save Room'}
                 </button>
               </div>
             </form>
           </div>
         </div>
       )}

       {/* Bed Add/Edit Dialog */}
       {bedDialogOpen && (
         <div className="dept-dialog-overlay" onClick={handleCloseBedDialog}>
           <div className="dept-dialog" onClick={e => e.stopPropagation()}>
             <div className="dept-dialog-header">
               <h6>{editingBed ? 'Edit Bed' : 'Add Bed'}</h6>
               <button className="dept-dialog-close" onClick={handleCloseBedDialog}>✕</button>
             </div>
             
             <form onSubmit={handleBedSubmit} className="dept-dialog-form">
               <div className="dept-dialog-row">
                 <div className="dept-dialog-field">
                   <label>Bed Name *</label>
                   <input
                     type="text"
                     value={bedFormData.name}
                     onChange={(e) => setBedFormData(prev => ({ ...prev, name: e.target.value }))}
                     required
                     disabled={loading}
                     placeholder="e.g., Bed A, Bed 1"
                   />
                 </div>
                 
                 <div className="dept-dialog-field">
                   <label>Bed Code *</label>
                   <input
                     type="text"
                     value={bedFormData.code}
                     onChange={(e) => setBedFormData(prev => ({ ...prev, code: e.target.value }))}
                     required
                     disabled={loading}
                     placeholder="e.g., BED-A, BED1"
                   />
                 </div>
               </div>

               <div className="dept-dialog-row">
                 <div className="dept-dialog-field">
                   <label>Bed Type *</label>
                   <select
                     value={bedFormData.type}
                     onChange={(e) => setBedFormData(prev => ({ ...prev, type: e.target.value }))}
                     required
                     disabled={loading}
                   >
                     <option value="">Select type...</option>
                     {BED_TYPES.map((type) => (
                       <option key={type} value={type}>{type}</option>
                     ))}
                   </select>
                 </div>
                 
                 <div className="dept-dialog-field">
                   <label>Status *</label>
                   <select
                     value={bedFormData.status}
                     onChange={(e) => setBedFormData(prev => ({ ...prev, status: e.target.value }))}
                     required
                     disabled={loading}
                   >
                     {BED_STATUS_OPTIONS.map((option) => (
                       <option key={option.value} value={option.value}>{option.label}</option>
                     ))}
                   </select>
                 </div>
               </div>

               <div className="dept-dialog-row">
                 <div className="dept-dialog-field">
                   <label>Notes</label>
                   <input
                     type="text"
                     value={bedFormData.notes}
                     onChange={(e) => setBedFormData(prev => ({ ...prev, notes: e.target.value }))}
                     disabled={loading}
                     placeholder="Additional notes..."
                   />
                 </div>
               </div>
               
               <div className="dept-dialog-actions">
                 <button type="button" className="dept-btn-cancel" onClick={handleCloseBedDialog}>
                   Cancel
                 </button>
                 <button type="submit" className="dept-btn-save" disabled={loading}>
                   {loading ? 'Saving...' : 'Save Bed'}
                 </button>
               </div>
             </form>
           </div>
         </div>
       )}
     </div>
   );
 };
 
 export default DepartmentManagement; 